import { NextRequest, NextResponse } from 'next/server';
import { logger } from '@/lib/logger';
import { constructWebhookEvent, stripe } from '@/lib/stripe/server';
import { getSupabaseAdmin } from '@/lib/supabase/server';
import type Stripe from 'stripe';
import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '@/lib/constants';

// Disable body parsing for webhook verification
export const runtime = 'nodejs';
export const dynamic = 'force-dynamic';

export async function POST(request: NextRequest) {
  try {
    // Get the raw body as text for signature verification
    const payload = await request.text();
    const signature = request.headers.get('stripe-signature');

    if (!signature) {
      return NextResponse.json({ error: 'Missing stripe-signature header' }, { status: 400 });
    }

    // Verify webhook signature and construct event
    let event: Stripe.Event;
    try {
      event = constructWebhookEvent(payload, signature);
    } catch (error) {
      logger.error('Webhook signature verification failed:', error);
      return NextResponse.json({ error: 'Invalid signature' }, { status: 400 });
    }

    const supabaseService = getSupabaseAdmin();

    // Handle specific event types
    switch (event.type) {
      case 'payment_intent.succeeded': {
        const paymentIntent = event.data.object as Stripe.PaymentIntent;
        await handlePaymentSuccess(supabaseService, paymentIntent);
        break;
      }

      case 'payment_intent.payment_failed': {
        const paymentIntent = event.data.object as Stripe.PaymentIntent;
        await handlePaymentFailure(supabaseService, paymentIntent);
        break;
      }

      case 'payment_intent.canceled': {
        const paymentIntent = event.data.object as Stripe.PaymentIntent;
        await handlePaymentCancellation(supabaseService, paymentIntent);
        break;
      }

      default:
        // Ignore unhandled event types
        break;
    }

    return NextResponse.json({ received: true });
  } catch (error) {
    logger.error('Webhook error:', error);
    return NextResponse.json({ error: 'Webhook processing failed' }, { status: HTTP_STATUS_INTERNAL_SERVER_ERROR });
  }
}

async function handlePaymentSuccess(
  supabase: ReturnType<typeof getSupabaseAdmin>,
  paymentIntent: Stripe.PaymentIntent
) {
  const matchId = paymentIntent.metadata?.match_id;
  
  if (!matchId) {
    logger.error('No match_id in payment intent metadata');
    throw new Error('No match_id in payment intent metadata');
  }

  // Update payment status to charged
  const { data: payment, error: paymentError } = await supabase
    .from('payments')
    .update({
      status: 'charged',
    })
    .eq('stripe_payment_id', paymentIntent.id)
    .select('id, match_id, company_id')
    .single();

  if (paymentError) {
    logger.error('Error updating payment status:', paymentError);
    throw new Error(`Failed to update payment status: ${paymentError.message}`);
  }

  if (!payment) {
    logger.error('Payment not found for intent:', paymentIntent.id);
    throw new Error(`Payment not found for intent: ${paymentIntent.id}`);
  }

  // Update match status to hired (only if not already hired)
  const { error: matchError } = await supabase
    .from('matches')
    .update({
      status: 'hired',
      contractor_action: 'accepted',
      hired_at: new Date().toISOString(),
      fee_charged: true,
    })
    .eq('id', matchId)
    .neq('status', 'hired');

  if (matchError) {
    logger.error('Error updating match status:', matchError);
    throw new Error(`Failed to update match status: ${matchError.message}`);
  }

  // Create notification for worker (non-critical, don't throw)
  const { data: match } = await supabase
    .from('matches')
    .select('worker_id, jobs(title)')
    .eq('id', matchId)
    .single();

  if (match) {
    const jobs = match.jobs as { title?: string } | null;
    const jobTitle = jobs?.title || 'a job';
    const { error: notifError } = await supabase.from('notifications').insert({
      profile_id: match.worker_id,
      type: 'hire_confirmed',
      title: 'You\'ve been hired!',
      body: `Congratulations! You've been hired for ${jobTitle}.`,
      data: { match_id: matchId, payment_id: payment.id },
    });
    if (notifError) {
      logger.error('Failed to create hire notification:', notifError);
    }
  }
}

async function handlePaymentFailure(
  supabase: ReturnType<typeof getSupabaseAdmin>,
  paymentIntent: Stripe.PaymentIntent
) {
  try {
    const { error } = await supabase
      .from('payments')
      .update({
        status: 'pending', // Keep as pending to allow retry
      })
      .eq('stripe_payment_id', paymentIntent.id);

    if (error) {
      logger.error('Error updating payment failure:', error);
    }
  } catch (error) {
    logger.error('Error handling payment failure:', error);
  }
}

async function handlePaymentCancellation(
  supabase: ReturnType<typeof getSupabaseAdmin>,
  paymentIntent: Stripe.PaymentIntent
) {
  try {
    const { error } = await supabase
      .from('payments')
      .delete()
      .eq('stripe_payment_id', paymentIntent.id)
      .eq('status', 'pending');

    if (error) {
      logger.error('Error deleting cancelled payment:', error);
    }
  } catch (error) {
    logger.error('Error handling payment cancellation:', error);
  }
}
